import React, { useEffect, useState } from 'react';
import MainCard from '@/components/MainCard';
import {
  Steps,
  Descriptions,
  Table,
  Empty,
  Flex,
  ConfigProvider,
  Modal,
  Input,
  Grid,
  Timeline,
  Typography as Typography1,
  Popconfirm,
  Card,
  Tag,
  Button,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Eye, CheckCircle, XCircle, ScrollText, ClipboardCheck, CircleCheck } from 'lucide-react';
import { defaultColors } from '@/themes/antd-theme';
import {
  bp_get_approvel_status_for_batch,
  bp_update_approval_status_for_recipe,
  bp_get_approval_status,
  get_api_function,
} from '@/utils/api';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import ReusableSearchInput from '@/components/ReusableSearch';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import DualESignatureAuth from '@/components/DualESignatureAuth';
import { accessControlUtils } from '@/components/AccessControlUtils';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/App';
import MBMRDetailsModal from '@/components/MBMRDetailsModal';
import { Typography } from 'antd';
const { Title } = Typography;

const BatchProcessApproval = ({ searchvalue }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [role, set_role] = useState('');
  const selectedModule = useSelector((state) => state.moduleView);
  const [recipe_data, set_recipe_data] = useState([]);
  const [loading, set_loading] = useState(false);
  const [showESignatureModal, setShowESignatureModal] = useState(false);
  const [pendingApprovalData, setPendingApprovalData] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [searchValue, setSearchValue] = useState('');
  const isESign = accessControlUtils('ESign')?.create_access || false;
  const [mbmrModal, setMbmrModal] = useState({ open: false, recipeId: null, title: '' });
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  //########## Handle Api's ##############################
  const get_all_dropDown_Data_on_submit = async () => {
    try {
      set_loading(true);
      await bp_get_approvel_status_for_batch(success_recipe_data);
    } catch (error) {
      set_loading(false);
      console.error('Error fetching data:', error);
    }
  };

  const success_recipe_data = (res) => {
    set_loading(false);
    set_recipe_data(res?.data?.data || []);
  };

  const success = async (res) => {
    if (res.status === 'success') {
      toast.success(res.message);
      try {
        await bp_get_approvel_status_for_batch(success_recipe_data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    } else if (res.status === 'failed') {
      toast.warning(res.message);
      set_loading(false);
    }
  };

  useEffect(() => {
    get_all_dropDown_Data_on_submit();
  }, []);

  // Handle approval/rejection with e-signature
  const handleApprovalAction = (record, action) => {
    if (isESign) {
      setPendingApprovalData(record);
      setApprovalAction(action);
      setShowESignatureModal(true);
    } else {
      setPendingApprovalData(null);
      setApprovalAction(null);
      submitApprovalAfterSignature({ ...record, action });
    }
  };

  // Submit approval after successful e-signature
  const submitApprovalAfterSignature = (signatureData) => {
    const recordData = pendingApprovalData ? pendingApprovalData : signatureData;
    if (!recordData) return;

    const payload = {
      process_id: recordData?.process_id,
      batch_number: recordData?.batch_number,
      batch_approval_id: recordData?.batch_approval_id,
      approval_status: signatureData?.action
        ? signatureData?.action === 'approve'
        : approvalAction === 'approve',
      signature_id: signatureData?.signature_id,
      comment: signatureData?.comment || signatureData?.reason || '',
    };

    bp_update_approval_status_for_recipe(payload, success);
    set_loading(true);
  };

  const warning = (record) => {
    let com = '';
    Modal.warning({
      title: 'Comment',
      cancelText: 'Cancel',
      closable: true,
      maskClosable: true,
      content: (
        <Input
          placeholder="Enter Comment"
          onChange={(e) => {
            com = e.target.value;
          }}
        />
      ),

      onOk() {
        bp_update_approval_status_for_recipe(
          {
            comment: com,
            process_id: record?.process_id,
            batch_number: record?.batch_number,
            batch_approval_id: record?.batch_approval_id,
            approval_status: false,
          },
          success
        );
        set_loading(true);
      },
    });
  };

  const [approvalStatusData, setApprovalStatusData] = useState([]);

  const allColumns = [
    {
      title: 'Batch Number',
      dataIndex: 'batch_number',
      key: 'batch_number',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (text) => text || '-',
    },
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (text) => text || '-',
    },
    {
      title: 'Recipe Name',
      dataIndex: 'recipe_name',
      key: 'recipe_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Process Name',
      dataIndex: 'process_name',
      key: 'process_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
    },
    {
      title: 'Process Type',
      dataIndex: 'is_dispensing_process',
      key: 'is_dispensing_process',
      defaultVisible: true,
      sortable: true,
      sortType: 'boolean',
      render: (value) => (
        <Tag color={value ? 'blue' : 'green'}>{value ? 'Dispensing' : 'Production'}</Tag>
      ),
    },
    {
      title: 'Batch Size',
      dataIndex: 'batch_size',
      key: 'batch_size',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
      render: (text, record) => {
        if (!text && !record?.batch_quantity) return '-';
        return `${text || record?.batch_quantity || '-'} ${record?.batch_unit || 'Units'}`;
      },
    },
    {
      title: 'Area/Location',
      dataIndex: 'area_section',
      key: 'area_section',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (area) => {
        if (!area) return '-';
        if (Array.isArray(area)) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {area.map((a, idx) => (
                <Tag key={idx} color="cyan">
                  {a}
                </Tag>
              ))}
            </div>
          );
        }
        return <Tag color="cyan">{area}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (priority) => {
        const priorityValue = priority || 'Medium';
        const color =
          priorityValue === 'High' ? 'red' : priorityValue === 'Low' ? 'default' : 'orange';
        return <Tag color={color}>{priorityValue}</Tag>;
      },
    },
    {
      title: 'Current Status',
      dataIndex: 'current_status',
      key: 'current_status',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (status) => {
        const statusValue = status || 'Pending Approval';
        const color = statusValue.includes('Approved')
          ? 'success'
          : statusValue.includes('Reject')
          ? 'error'
          : 'warning';
        return <Tag color={color}>{statusValue}</Tag>;
      },
    },
    {
      title: 'Submitted By',
      dataIndex: 'submitted_by',
      key: 'submitted_by',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      render: (text, record) => text || record?.created_by || '-',
    },
    {
      title: 'Submitted Date',
      dataIndex: 'submitted_date',
      key: 'submitted_date',
      defaultVisible: true,
      sortable: true,
      sortType: 'date',
      render: (text, record) => {
        const dateValue = text || record?.created_date;
        if (!dateValue) return '-';
        try {
          return new Date(dateValue).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch {
          return dateValue;
        }
      },
    },
    {
      title: 'Scheduled Date',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      defaultVisible: true,
      sortable: true,
      sortType: 'date',
      render: (text) => {
        if (!text) return '-';
        try {
          return new Date(text).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
        } catch {
          return text;
        }
      },
    },
    {
      title: 'Estimated Duration',
      dataIndex: 'estimated_duration',
      key: 'estimated_duration',
      defaultVisible: true,
      sortable: true,
      sortType: 'number',
      render: (duration, record) => {
        const durationValue = duration || record?.process_duration;
        if (!durationValue) return '-';
        return `${durationValue} min`;
      },
    },
    {
      title: 'Assigned Roles',
      dataIndex: 'emp_roles',
      key: 'emp_roles',
      defaultVisible: true,
      sortable: false,
      render: (roles) => {
        if (!roles || !Array.isArray(roles) || roles.length === 0) return '-';
        return (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              maxHeight: '60px',
              overflowY: 'auto',
            }}
          >
            {roles.map((role, idx) => (
              <Tag key={idx} color="purple">
                {role}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: 'View Approvals',
      key: 'view_approvals',
      align: 'center',
      defaultVisible: true,
      sortable: false,
      fixed: 'right',
      width: 120,
      render: (_, record) => {
        return (
          <Tooltip title="View Approval Flow">
            <Button
              type="text"
              className="transition-transform duration-200 hover:scale-125"
              icon={<ClipboardCheck size={20} style={{ color: defaultColors.primary }} />}
              onClick={() => {
                setIsModalOpen(true);
                set_role(record?.role_name);
                get_api_function(
                  `/get_batch_approval_flow?batch_number=${record?.batch_number}&process_id=${record?.process_id}`,
                  (res) => {
                    if (res.data.status === 'success') setApprovalStatusData(res.data.data);
                  }
                );
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'MBMR Details',
      key: 'mbmrDetails',
      width: 110,
      align: 'center',
      defaultVisible: true,
      alwaysVisible: true,
      sortable: false,
      render: (_, record) => (
        <div className="flex justify-center">
          <Tooltip title="View MBMR Details">
            <Button
              type="text"
              className="transition-transform duration-200 hover:scale-125"
              icon={<ScrollText size={20} style={{ color: '#1890ff' }} />}
              onClick={() => {
                if (!record?.recipe_id) return;
                setMbmrModal({ open: true, recipeId: record.recipe_id, title: record?.recipe_name || 'MBMR Details' });
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      defaultVisible: true,
      sortable: false,
      fixed: 'right',
      width: 150,
      render: (_, record) => {
        return (
          <div className="flex flex-row gap-2 justify-center">
            <Tooltip title="Approve">
              <Button
                type="text"
                className="transition-transform duration-200 hover:scale-125"
                icon={<CircleCheck size={20} style={{ color: defaultColors.success }} />}
                onClick={() => {
                  handleApprovalAction(record, 'approve');
                }}
              />
            </Tooltip>
            <Tooltip title="Reject">
              <Button
                type="text"
                className="transition-transform duration-200 hover:scale-125"
                icon={<XCircle size={20} style={{ color: defaultColors.error }} />}
                onClick={() => {
                  handleApprovalAction(record, 'reject');
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const addKeysInFilterDataForCollapse = (filterData) => {
    const updatedFilterList = filterData?.map((item, index) => {
      return {
        ...item,
        key: index.toString(), // Update the key to the index converted to a string
      };
    });
    return updatedFilterList;
  };

  const { visibleColumns, columnConfigMenu, filteredData } = useTableConfig({
    tableKey: 'batch_process_approval_config',
    columns: allColumns,
    data: addKeysInFilterDataForCollapse(recipe_data),
    searchValue: searchValue,
  });

  const get_Ingredients = (record) => {
    return (
      <Table
        size="small"
        bordered
        title={() => 'Ingredients'}
        dataSource={record?.ingredients}
        columns={[
          {
            title: 'Ingredient Name',
            dataIndex: 'ingredient_name',
            key: 'ingredient_name',
          },
          {
            title: 'Condition',
            dataIndex: 'conditinal_para',
            key: 'conditinal_para',
          },
          {
            title: 'Quantity',
            key: 'quantity',
            render: (_, record) => `${record?.ingredient_qty}${' '}${record?.unit}`,
          },
        ]}
        pagination={false}
      />
    );
  };

  const get_sampling = (record) => {
    return (
      <Table
        size="small"
        bordered
        title={() => 'Sampling'}
        dataSource={record?.sampling}
        columns={[
          {
            title: 'Material Inventory Name',
            dataIndex: 'material_inventory_name',
            key: 'material_inventory_name',
          },
          {
            title: 'Testing Type Name',
            dataIndex: 'testing_type_name',
            key: 'testing_type_name',
          },
          {
            title: 'Sample Number',
            dataIndex: 'sample_number',
            key: 'sample_number',
          },
          {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (_, record) => `${record?.quantity}${' '}${record?.material_inventory_unit}`,
          },
          {
            title: 'Sample Quantity',
            dataIndex: 'sample_quantity',
            key: 'sample_quantity',
            render: (_, record) =>
              `${record?.sample_quantity}${' '}${record?.material_inventory_unit}`,
          },
        ]}
        pagination={false}
      />
    );
  };

  const get_Product = (record) => {
    return (
      <p style={{ margin: 0 }}>
        <Table
          size="small"
          bordered
          title={() => 'Product'}
          dataSource={record?.products}
          columns={[
            {
              title: 'Product Name',
              dataIndex: 'product_name',
              key: 'product_name',
            },
            {
              title: 'value',
              key: 'value',
              render: (_, record) => {
                return (
                  record?.value?.length > 0 && (
                    <Flex gap="20px" vertical={true} justify="flex-start">
                      <Table
                        size="small"
                        bordered
                        dataSource={record?.value}
                        columns={[
                          {
                            title: 'Container Id',
                            dataIndex: 'container_id',
                            key: 'created_on',
                          },
                          {
                            title: 'Tare Wt',
                            dataIndex: 'tare_weight',
                            key: 'value',
                          },
                          {
                            title: 'Gross Wt',
                            dataIndex: 'gross_weight',
                            key: 'value',
                          },
                          {
                            title: 'Net Wt',
                            dataIndex: 'net_weight',
                            key: 'value',
                          },
                        ]}
                        pagination={false}
                      />
                    </Flex>
                  )
                );
              },
            },
          ]}
          pagination={false}
        />
      </p>
    );
  };

  const get_Parmeters = (record) => {
    return (
      <Table
        size="small"
        bordered
        title={() => 'Parameters'}
        dataSource={record?.paramaters}
        columns={[
          {
            title: 'Parameter Name',
            dataIndex: 'parameter_name',
            key: 'parameter_name',
          },
          {
            title: 'value',
            key: 'value',
            render: (_, record) => {
              return (
                record?.value?.length > 0 && (
                  <Table
                    size="small"
                    bordered
                    dataSource={record?.value}
                    columns={[
                      {
                        title: 'Created On',
                        dataIndex: 'created_on',
                        key: 'created_on',
                        render: (created_on) => created_on?.slice(0, 19),
                      },
                      {
                        title: 'Value',
                        dataIndex: 'value',
                        key: 'value',
                      },
                    ].filter(Boolean)}
                    pagination={false}
                  />
                )
              );
            },
          },
        ]}
        pagination={false}
      />
    );
  };

  const columns = [
    {
      title: 'Op No',
      dataIndex: 'operation_number',
      key: 'operation_number',
    },
    {
      title: 'Operation',
      dataIndex: 'operation_type',
      key: 'operation_type',
    },
    {
      title: 'Machine',
      dataIndex: 'machine_name',
      key: 'machine_id',
    },
    // {
    //   title: "Instruction",
    //   dataIndex: "instruction",
    //   key: "instruction",
    // },
    {
      title: 'Total Duration',
      dataIndex: 'total_duration',
      key: 'total_duration',
      render: (_, record) => {
        return record?.total_duration;
        // <Descriptions
        //   size="small"
        //   column={1}
        //   bordered
        //   items={[
        //     {
        //       key: 0,
        //       label: "Min",
        //       children: record?.min_duration,
        //     },
        //     {
        //       key: 1,
        //       label: "Max",
        //       children: record?.max_duration,
        //     },
        //     {
        //       key: 2,
        //       label: "Total",
        //       children: record?.total_duration,
        //     },
        //   ]}
        // />
      },
    },
    {
      title: 'Details ',
      key: 'detailsRemarks',
      align: 'left',
      render: (_, record) => {
        return (
          <Flex gap="20px" vertical={true} justify="flex-start">
            {record?.ingredients?.length > 0 && get_Ingredients(record)}
            {record?.paramaters?.length > 0 && get_Parmeters(record)}
            {record?.products?.length > 0 && get_Product(record)}
            {record?.sampling?.length > 0 && get_sampling(record)}
          </Flex>
        );
      },
    },
  ];

  const expandableRowRender = (record) => {
    return (
      <p style={{ margin: 0 }}>
        <Table
          dataSource={record?.operations}
          columns={columns}
          pagination={false}
          scroll={{ x: 'max-content' }}
          bordered
        />
      </p>
    );
  };

  const approval_status_json = {
    approve: <CheckCircleOutlined style={{ color: '#5DCC79', fontSize: '16px' }} />,
    Pending: <ClockCircleOutlined style={{ color: '#FAAE2F', fontSize: '16px' }} />,
  };

  const status_tag_json = {
    approve: <Tag color="success">Approved</Tag>,
    Pending: <Tag color="warning">Pending</Tag>,
  };

  return (
    <div className="p-0.5 min-h-screen" style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}>
      <Card
        className="shadow-sm"
        bordered={false}
        style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff' }}
        title={
          <div className="flex items-center gap-2">
            <Icons.CheckCircle className="text-blue-600" size={24} />
            <Title
              level={4}
              style={{
                color: textColor,
                margin: 0,
                padding: 0,
              }}
            >
              {t('Batch Process Approval')}
            </Title>
          </div>
        }
        extra={
        <div className="flex flex-row gap-2 items-center">
          <TableColumnConfig columns={allColumns} tableKey="batch_process_approval_config" />
          <Input
            id="search-batch-process-approval"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            prefix={<SearchOutlined />}
            className="w-64"
          />
        </div>
      }
    >
      <Table
        columns={visibleColumns}
        bordered
        scroll={{ x: 'max-content' }}
        dataSource={filteredData}
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      <Modal
        title="Approvals"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[]}
      >
        <Timeline
          mode="alternate"
          items={approvalStatusData?.map((item) => {
            return {
              dot: approval_status_json[item?.status],
              children: (
                <>
                  <Typography1>{item?.role_name}</Typography1>
                  {status_tag_json[item?.status]}
                </>
              ),
            };
          })}
        />
      </Modal>

      {/* E-Signature Modal for Approval/Rejection */}
      <DualESignatureAuth
        visible={showESignatureModal}
        onCancel={() => {
          setShowESignatureModal(false);
          setPendingApprovalData(null);
          setApprovalAction(null);
        }}
        onSubmit={(signatureData) => {
          submitApprovalAfterSignature(signatureData?.signature_data || signatureData);
          setShowESignatureModal(false);
          setPendingApprovalData(null);
          setApprovalAction(null);
        }}
        signatureType={
          approvalAction === 'approve' ? 'BATCH_PROCESS_APPROVE' : 'BATCH_PROCESS_REJECT'
        }
        action_type={
          approvalAction === 'approve' ? 'BATCH_PROCESS_APPROVE' : 'BATCH_PROCESS_REJECT'
        }
        signatureMeaning={
          approvalAction === 'approve' ? 'Batch Process Approval' : 'Batch Process Rejection'
        }
        documentType="BATCH_PROCESS"
        batch_number={pendingApprovalData?.batch_number || ''}
        recipe_id={pendingApprovalData?.recipe_id || ''}
        documentId={pendingApprovalData?.batch_number || ''}
        documentNumber={pendingApprovalData?.batch_number || ''}
        documentData={{
          batch_number: pendingApprovalData?.batch_number || '',
          process_id: pendingApprovalData?.process_id || '',
          process_name: pendingApprovalData?.process_name || '',
          recipe_name: pendingApprovalData?.recipe_name || '',
          approval_action: approvalAction,
        }}
        config={{
          enableBasic: true,
          enableCFR: true,
          defaultMode: 'cfr',
          requireComment: true,
          title: `E-Signature for ${approvalAction === 'approve' ? 'Approval' : 'Rejection'}`,
        }}
      />
      </Card>

      <MBMRDetailsModal
        open={mbmrModal.open}
        recipeId={mbmrModal.recipeId}
        title={mbmrModal.title}
        onClose={() => setMbmrModal({ open: false, recipeId: null, title: '' })}
      />
    </div>
  );
};

export default BatchProcessApproval;
